/**
 * HbarReleaseTopBarBadge — V2.J.3
 *
 * Compact TopBar indicator that surfaces a recent HBAR treasury
 * release at the global navigation level. Renders nothing if no
 * release was detected in the last `maxAgeDays` days.
 *
 * Behaviour :
 *  - Pulsing ℏ chip in Hedera cyan.
 *  - Click navigates to /hedera-monitor (or via `onNavigate` if
 *    supplied — the route name we use is "hedera-monitor", aligned
 *    with the existing TAB_ROUTES in App.tsx).
 *  - Persists a "dismissed-at" timestamp in localStorage so the user
 *    can mute the badge for the current event without us forgetting
 *    forever — when the next event arrives, the badge re-appears.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHbarReleaseEvents } from '../../hooks/useHbarReleaseEvents';

const DISMISS_STORAGE_KEY = 'hbar_release_topbar_dismissed_event_id';

interface HbarReleaseTopBarBadgeProps {
  /** Max age (days) before the badge stops appearing. Default 7. */
  maxAgeDays?: number;
}

export function HbarReleaseTopBarBadge({
  maxAgeDays = 7,
}: HbarReleaseTopBarBadgeProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { latest } = useHbarReleaseEvents({ limit: 1 });

  // Hydrate dismissed-event from localStorage once. We don't write
  // back during render — only on the user click below.
  const [dismissedEventId, setDismissedEventId] = useState<string | null>(() => {
    try {
      return typeof window !== 'undefined'
        ? window.localStorage.getItem(DISMISS_STORAGE_KEY)
        : null;
    } catch {
      return null;
    }
  });

  // Re-sync from storage when the event id changes — so dismiss state
  // doesn't survive a fresh release.
  useEffect(() => {
    try {
      const stored =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(DISMISS_STORAGE_KEY)
          : null;
      setDismissedEventId(stored);
    } catch {
      /* ignore */
    }
  }, [latest?.id]);

  const isRecent = useMemo(() => {
    if (!latest) return false;
    const ageMs = Date.now() - new Date(latest.detected_at).getTime();
    return ageMs <= maxAgeDays * 24 * 3600 * 1000;
  }, [latest, maxAgeDays]);

  const shouldShow = isRecent && latest && latest.id !== dismissedEventId;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Mark this event as seen — keeps the badge from haunting the
      // TopBar after the user opens the monitor.
      try {
        if (latest?.id) {
          window.localStorage.setItem(DISMISS_STORAGE_KEY, latest.id);
          setDismissedEventId(latest.id);
        }
      } catch {
        /* ignore */
      }
      navigate('/hedera-monitor');
    },
    [latest?.id, navigate],
  );

  // Inject the pulse keyframes once (idempotent).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('hbar-topbar-badge-pulse')) return;
    const style = document.createElement('style');
    style.id = 'hbar-topbar-badge-pulse';
    style.textContent = `
      @keyframes hbarTopBarBadgePulse {
        0%   { box-shadow: 0 0 0 0 rgba(0, 194, 255, 0.6); }
        70%  { box-shadow: 0 0 0 8px rgba(0, 194, 255, 0); }
        100% { box-shadow: 0 0 0 0 rgba(0, 194, 255, 0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  if (!shouldShow) return null;

  return (
    <a
      href="/hedera-monitor"
      onClick={handleClick}
      className="icon-btn"
      aria-label={t(
        'hedera.topBarBadge.aria',
        'Libération HBAR récente détectée — ouvrir le moniteur Hedera',
      )}
      title={t('hedera.topBarBadge.title', 'Libération HBAR détectée')}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        color: '#00C2FF',
        fontSize: '18px',
        fontWeight: 700,
        animation: 'hbarTopBarBadgePulse 2s ease-in-out infinite',
        borderRadius: '50%',
      }}
    >
      ℏ
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: '4px',
          right: '4px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#FF5252',
          border: '1.5px solid var(--bg, #0b0b0e)',
        }}
      />
    </a>
  );
}
